import type { ReactNode } from 'react';
import { SIGNUP_URL } from '../../lib/appUrls';

/**
 * Pieces shared by blog posts. Styles: .bl-* and .bw-* in src/styles/blog.css.
 *
 * Every interactive figure renders a complete default state on the server, so
 * the prerendered HTML already shows the picture and the prose around it. The
 * self-playing figures (Sequence.tsx) start when scrolled into view and only
 * change which state is shown; the server render is their final frame.
 */

/** A figure that may run wider than the reading column. */
export function Figure({ children, caption }: { children: ReactNode; caption?: ReactNode }) {
  return (
    <figure className="bl-fig">
      {children}
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

/** The frame every interactive figure sits in: a title row with controls, then the body. */
export function Widget({ title, controls, children }: { title: ReactNode; controls?: ReactNode; children: ReactNode }) {
  return (
    <div className="bw">
      <div className="bw-top">
        <h3>{title}</h3>
        {controls}
      </div>
      {children}
    </div>
  );
}

/** A two or three way toggle. `aria-pressed` carries the state for assistive tech. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="bw-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Sign-up link for posts. `ref` tells the app which post sent the visitor. */
export function signupFrom(ref: string): string {
  return `${SIGNUP_URL}?ref=${encodeURIComponent(ref)}`;
}

/** Inline call to action. A plain anchor, so it is a real link in the prerendered HTML. */
export function InlineCta({
  refTag,
  children,
  label = 'Start free',
}: {
  refTag: string;
  children: ReactNode;
  label?: string;
}) {
  return (
    <aside className="bl-cta">
      <p>{children}</p>
      <a className="s-btn s-btn-pri" href={signupFrom(refTag)} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    </aside>
  );
}
